import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  CardActions, 
  Typography, 
  Container, 
  CircularProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useToast } from '../../contexts/ConfirmContext';

type Plan = {
  id: 'base_monthly' | 'base_yearly' | 'premium_monthly' | 'premium_yearly';
  name: string;
  price: string;
  pricePerMonth: string;
  description: string;
  features: string[];
  isPopular?: boolean;
};

const plans: Plan[] = [
  {
    id: 'base_monthly',
    name: 'Base Monthly',
    price: '£10.00',
    pricePerMonth: '£10.00/month',
    description: 'Core scheduling & progress',
    features: [
      'Calendar management',
      'Student profile & tracking',
      'DVSA progress checklist',
      'Theory & mock test logging',
      'Email notifications',
      'WhatsApp module (Locked)'
    ]
  },
  {
    id: 'base_yearly',
    name: 'Base Yearly',
    price: '£99.00',
    pricePerMonth: '£8.25/month',
    description: 'Billed annually (save £21)',
    features: [
      'Everything in Base Monthly',
      'Priority email support',
      'WhatsApp module (Locked)'
    ]
  },
  {
    id: 'premium_monthly',
    name: 'Premium Monthly',
    price: '£15.00',
    pricePerMonth: '£15.00/month',
    description: 'Includes WhatsApp reminders',
    isPopular: true,
    features: [
      'Everything in Base Monthly',
      'WhatsApp automated notifications',
      'Automated lesson reminders',
      'WhapiCell pairing tool',
      'WhatsApp bulk messaging'
    ]
  },
  {
    id: 'premium_yearly',
    name: 'Premium Yearly',
    price: '£149.00',
    pricePerMonth: '£12.42/month',
    description: 'Billed annually (save £31)',
    features: [
      'Everything in Premium Monthly',
      'Priority support',
      'Early access to new features'
    ]
  }
];

const SubscribePage: React.FC = () => {
  const showToast = useToast();
  const { hasActiveWhatsAppAddon, isLoading, createPayment } = useSubscription();
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Bank transfer states
  const [openDialog, setOpenDialog] = useState(false);
  const [paymentReference, setPaymentReference] = useState('');
  const [submittingBT, setSubmittingBT] = useState(false);
  const [btSuccess, setBtSuccess] = useState(false);
  const [successPayment, setSuccessPayment] = useState<any | null>(null);

  const isPlanActive = (planId: string) => {
    // In our UAT setup, hasActiveSubscription is bypassed to true
    if (planId.startsWith('premium_')) {
      return hasActiveWhatsAppAddon;
    } else {
      return !hasActiveWhatsAppAddon;
    }
  };

  const handleSubscribe = (plan: Plan) => {
    setSelectedPlan(plan);
    setPaymentReference('');
    setOpenDialog(true);
  };

  const handleConfirmBankTransfer = async () => {
    if (!selectedPlan) return;
    setError(null);
    setSubmittingBT(true);

    try {
      let amount = 10.00;
      if (selectedPlan.id === 'base_monthly') amount = 10.00;
      else if (selectedPlan.id === 'base_yearly') amount = 99.00;
      else if (selectedPlan.id === 'premium_monthly') amount = 15.00;
      else if (selectedPlan.id === 'premium_yearly') amount = 149.00;

      const isMonthly = selectedPlan.id.includes('monthly');
      
      // Call createPayment with BankTransfer method and sender reference notes
      const payment = await createPayment(amount, !isMonthly, 'BankTransfer', `Sender Ref: ${paymentReference}`);
      
      if (payment) {
        setSuccessPayment(payment);
        setOpenDialog(false);
        setBtSuccess(true);
      } else {
        throw new Error('Failed to submit bank transfer request');
      }
    } catch (err) {
      console.error('Bank transfer submission error:', err);
      setError('Failed to submit payment request. Please try again.');
    } finally {
      setSubmittingBT(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Box textAlign="center" mb={6}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          Choose Your Plan
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Select the plan that works best for you
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
        {plans.map((plan) => (
          <Box key={plan.id} sx={{ width: { xs: '100%', sm: '45%', md: '45%', lg: '22%' }, minWidth: '260px', maxWidth: '320px' }}>
            <Card 
              elevation={isPlanActive(plan.id) ? 8 : (plan.isPopular ? 6 : 2)} 
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: isPlanActive(plan.id) ? '2px solid #2e7d32' : (plan.isPopular ? '2px solid #1976d2' : 'none'),
                position: 'relative',
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-8px)',
                },
              }}
            >
              {isPlanActive(plan.id) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 16,
                    transform: 'translateY(-50%)',
                    bgcolor: 'success.main',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Active Plan
                </Box>
              )}
              {!isPlanActive(plan.id) && plan.isPopular && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 16,
                    transform: 'translateY(-50%)',
                    bgcolor: 'primary.main',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Most Popular
                </Box>
              )}
              
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {plan.name}
                </Typography>
                <Box mb={3}>
                  <Box display="flex" alignItems="baseline" mb={1}>
                    <Typography variant="h3" component="div" color={isPlanActive(plan.id) ? 'success.main' : 'primary'} sx={{ fontWeight: 'bold' }}>
                      {plan.price}
                    </Typography>
                    {plan.id.includes('yearly') && (
                      <Typography variant="subtitle1" color="text.secondary" ml={1}>
                        /year
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {plan.pricePerMonth} • {plan.description}
                  </Typography>
                </Box>
                
                <List dense disablePadding>
                  {plan.features.map((feature, index) => (
                    <ListItem key={index} disableGutters disablePadding sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleIcon color={isPlanActive(plan.id) ? 'success' : 'primary'} fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
              
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                  fullWidth
                  variant={isPlanActive(plan.id) ? 'outlined' : 'contained'}
                  color={isPlanActive(plan.id) ? 'success' : 'primary'}
                  size="large"
                  disabled={(submittingBT && selectedPlan?.id === plan.id) || isPlanActive(plan.id)}
                  onClick={() => handleSubscribe(plan)}
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                  }}
                >
                  {submittingBT && selectedPlan?.id === plan.id ? (
                    <>
                      <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                      Processing...
                    </>
                  ) : isPlanActive(plan.id) ? (
                    'Your Plan'
                  ) : (
                    'Upgrade'
                  )}
                </Button>
              </CardActions>
            </Card>
          </Box>
        ))}
      </Box>
      
      <Box mt={6} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          Need help choosing a plan? <button 
            onClick={() => showToast('Contact sales team at sales@adilessons.com', 'info')} 
            style={{ 
              color: '#1976d2', 
              textDecoration: 'none', 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer',
              padding: 0,
              font: 'inherit'
            }}
          >
            Contact our sales team
          </button>
        </Typography>
      </Box>

      {/* Bank Transfer Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={!submittingBT ? () => setOpenDialog(false) : undefined}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#1a237e', pb: 1 }}>
          Bank Transfer (BACS) Payment
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            To activate the <strong>{selectedPlan?.name}</strong>, please transfer <strong>{selectedPlan?.price}</strong> to the following HSBC UK bank account.
          </DialogContentText>
          
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: 2, 
              bgcolor: 'rgba(25, 118, 210, 0.02)',
              borderColor: 'rgba(25, 118, 210, 0.15)'
            }}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Account Holder</Typography>
                <Typography variant="body1" fontWeight="bold" color="#1a237e">Osman Dolukup</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Bank Name</Typography>
                <Typography variant="body1" fontWeight="bold" color="#1a237e">HSBC UK</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Sort Code</Typography>
                <Typography variant="body1" fontWeight="bold" color="#1a237e">40-05-00</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Account Number</Typography>
                <Typography variant="body1" fontWeight="bold" color="#1a237e">30006149</Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Amount to Transfer</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">{selectedPlan?.price}</Typography>
              </Grid>
            </Grid>
          </Paper>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            After making the transfer, please enter your <strong>Sender Name / Reference</strong> below so we can verify and activate your account immediately.
          </Typography>

          <TextField
            autoFocus
            margin="dense"
            label="Your Sender Name / Reference (e.g. Osman Dolukup EFT)"
            fullWidth
            variant="outlined"
            value={paymentReference}
            onChange={(e) => setPaymentReference(e.target.value)}
            disabled={submittingBT}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setOpenDialog(false)} 
            disabled={submittingBT}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmBankTransfer} 
            disabled={submittingBT || !paymentReference.trim()}
            variant="contained"
            color="primary"
            sx={{ fontWeight: 'bold', px: 3 }}
          >
            {submittingBT ? <CircularProgress size={24} color="inherit" /> : 'Confirm Payment Sent'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog 
        open={btSuccess} 
        onClose={() => { setBtSuccess(false); window.location.reload(); }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            textAlign: 'center'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: 'success.main' }}>
          Payment Submitted Successfully!
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Your bank transfer request for <strong>{selectedPlan?.price}</strong> has been registered.
          </DialogContentText>
          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, mb: 2 }}>
            <Typography variant="caption" color="textSecondary">Transaction Reference</Typography>
            <Typography variant="body1" fontWeight="bold"><code>{successPayment?.transactionId}</code></Typography>
          </Box>
          <DialogContentText>
            The administrator will review the transfer and activate your subscription shortly. Thank you!
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            onClick={() => { setBtSuccess(false); window.location.reload(); }} 
            variant="contained" 
            color="success" 
            sx={{ fontWeight: 'bold', px: 4 }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SubscribePage;
